import { forwardRef } from "react";
import { Info } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { AnimatedNumber, fmtInt } from "@/components/silver/common/AnimatedNumber";
import { ReachVisual } from "@/components/silver/results/ReachVisual";
import { DataTip } from "@/components/silver/common/Reveal";
import { motion } from "framer-motion";

export const areasText = (areas) => (areas.length <= 4 ? areas.join(" + ") : `${areas.slice(0, 3).join(", ")} + ${areas.length - 3} more districts`);

const densityLabel = (ratio) => (ratio >= 1.5 ? "Strong local audience" : ratio >= 0.75 ? "Typical local audience" : "Smaller local audience");

export const AudienceHeadline = forwardRef(function AudienceHeadline(_, ref) {
  const { audience, audienceLabel, setHowOpen } = useSilver();
  const { benchmark } = audience;
  const share = audience.silverTotal ? audience.total / audience.silverTotal : 0;

  return (
    <motion.div className="glass relative overflow-hidden p-7 sm:p-10" initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }} data-testid="audience-headline">
      <div className="pointer-events-none absolute -left-24 -top-32 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.28),transparent_62%)]" aria-hidden="true" />
      <div className="relative grid items-center gap-8 md:grid-cols-[1.05fr_1fr]">
        <div className="min-w-0">
          <p className="eyebrow">Your homeowner audience</p>
          <h2 ref={ref} tabIndex={-1} className="mt-5 outline-none">
            <span className="sr-only">{fmtInt(audience.total)} matching households</span>
            <AnimatedNumber value={audience.total} duration={2.4} delay={0.5} aria-hidden="true"
              className="num block font-extrabold leading-[0.85] tracking-[-0.06em] text-[#C6EE5A] text-glow" style={{ fontSize: "clamp(4rem, 8.6vw, 8rem)" }} data-testid="results-total-households" />
          </h2>
          <p className="mt-5 text-xl font-bold tracking-[-0.02em] text-white sm:text-2xl">matching households</p>
          <p className="mt-1 text-[15px] text-white/60" data-testid="results-areas-text">across <span className="font-semibold text-white">{areasText(audience.areas)}</span></p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="chip-lime rounded-full px-3.5 py-1.5 text-[12px] font-semibold" data-testid="results-audience-label">{audienceLabel}</span>
            <span className="pill !py-1.5 text-[12px]">{audience.matchedProfiles} of {audience.profileCount} profiles</span>
          </div>
        </div>
        <ReachVisual />
      </div>

      <div className="relative mt-9 grid gap-6 border-t border-white/10 pt-7 sm:grid-cols-2">
        <div data-testid="density-indicator">
          <p className="eyebrow-muted flex items-center gap-2">
            Local density
            <DataTip label={`Average matching households per selected district, compared with the average across all ${benchmark.districtCount.toLocaleString("en-GB")} districts in the dataset using the same criteria.`}>
              <button type="button" aria-label="About local density" className="text-white/40 hover:text-white"><Info className="h-3.5 w-3.5" /></button>
            </DataTip>
          </p>
          <p className="mt-2 flex items-baseline gap-2">
            <AnimatedNumber value={benchmark.ratio} format={(v) => `${v.toFixed(1)}×`} className="num text-3xl font-extrabold tracking-[-0.03em] text-white" />
            <span className="text-[13px] text-white/55">the dataset average</span>
          </p>
          <p className="mt-1 text-[13px] font-semibold text-[#C6EE5A]">{densityLabel(benchmark.ratio)}</p>
        </div>
        <div data-testid="share-indicator">
          <p className="eyebrow-muted">Share of your local homeowner market</p>
          <p className="mt-2 flex items-baseline gap-2">
            <AnimatedNumber value={share * 100} format={(v) => `${Math.round(v)}%`} className="num text-3xl font-extrabold tracking-[-0.03em] text-white" />
            <span className="text-[13px] text-white/55">of {fmtInt(audience.silverTotal)} homeowner households</span>
          </p>
          <button type="button" onClick={() => setHowOpen(true)} className="mt-1 inline-flex items-center gap-1.5 text-[13px] text-white/50 underline-offset-4 hover:text-white hover:underline" data-testid="results-how-calculated-btn">
            <Info className="h-3.5 w-3.5" /> How is this calculated?
          </button>
        </div>
      </div>
    </motion.div>
  );
});
